import React, { useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

// Hook returning an openKit handler: gates on auth, downloads/opens the kit.
export function useKitAccess(setGateOpen, setPendingNext) {
  const { user } = useAuth();
  const navigate = useNavigate();

  return useCallback(
    async (kit) => {
      const authed = user && typeof user === "object";
      if (!authed) {
        setPendingNext(`/revision-kits`);
        setGateOpen(true);
        return;
      }
      try {
        const res = await api.get(`/kits/${kit.id}/download`, { responseType: "blob" });
        const ct = res.headers["content-type"] || "";
        if (ct.includes("application/json")) {
          const text = await res.data.text();
          const json = JSON.parse(text);
          if (json.type === "external" && json.url) {
            window.open(json.url, "_blank", "noopener");
            return;
          }
        }
        const blob = new Blob([res.data], { type: ct || "application/pdf" });
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = kit.original_filename || `${kit.title}.pdf`;
        document.body.appendChild(a);
        a.click();
        a.remove();
        URL.revokeObjectURL(url);
        toast.success("Download started");
      } catch (e) {
        if (e?.response?.status === 401) {
          setPendingNext(`/revision-kits`);
          setGateOpen(true);
        } else if (e?.response?.status === 404) {
          toast.info("This kit's file will be uploaded soon. Check back shortly!");
        } else {
          toast.error("Could not open the kit. Please try again.");
        }
      }
    },
    [user, navigate, setGateOpen, setPendingNext]
  );
}
